// Campaign assets - things that end up inside a published customer campaign
// (logos, markers, AR objects, backgrounds). Served from the locked-down
// approved bucket via the user-app at runtime, not directly by the campaign
// creator's browser. For platform/site UI assets (icons, splash decorations)
// see frontend/src/config/platformAssets.ts and /AGENTS.md at the repo root.
import { buildApprovedAssetUrls } from '@/config/assetBuckets';
import {
  backgroundOptionThumbnail,
  logoOptionThumbnail,
} from '@/config/platformAssets';

export interface DefaultAsset {
  id: string;
  name: string;
  type: 'logo' | 'mapMarker' | 'ar' | 'background';
  s3Url: string;
  s3Path: string;
  thumbnailUrl: string;
}

const formatDisplayName = (name: string): string => {
  return name
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const createAsset = (
  id: string,
  name: string,
  type: DefaultAsset['type'],
  storagePath: string,
  thumbnailUrl: string,
): DefaultAsset => {
  const { httpUrl, s3Path } = buildApprovedAssetUrls(`defaults/${storagePath}`);

  return {
    id,
    name,
    type,
    s3Url: httpUrl,
    s3Path,
    thumbnailUrl,
  };
};

type AssetThumbnailModule = { default: string };

type AssetDirectoryConfig = {
  modules: Record<string, AssetThumbnailModule>;
  storageFolder: string;
  idPrefix: string;
  displayNamePrefix?: string;
  type: DefaultAsset['type'];
};

const buildAssetsFromDirectory = ({
  modules,
  storageFolder,
  idPrefix,
  displayNamePrefix,
  type,
}: AssetDirectoryConfig): DefaultAsset[] => {
  const entries = Object.entries(modules)
    .map(([modulePath, module]) => {
      const fileName = modulePath.substring(modulePath.lastIndexOf('/') + 1);
      if (!fileName) {
        return null;
      }

      const identifier = fileName.replace(/\.png$/, '');
      const displayName = formatDisplayName(identifier);

      return {
        asset: createAsset(
          `${idPrefix}-${identifier}`,
          displayNamePrefix ? `${displayNamePrefix} ${displayName}` : displayName,
          type,
          `${storageFolder}/${fileName}`,
          module.default,
        ),
        sortKey: identifier,
      };
    })
    .filter(
      (entry): entry is { asset: DefaultAsset; sortKey: string } => Boolean(entry),
    )
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true }));

  return entries.map(({ asset }) => asset);
};

const markerThumbnailModules = import.meta.glob(
  '../assets/Campaign Creator Assets/Map Markers/*.png',
  { eager: true },
) as Record<string, AssetThumbnailModule>;

const arThumbnailModules = import.meta.glob(
  '../assets/Campaign Creator Assets/AR Assets/glb icons/*.png',
  { eager: true },
) as Record<string, AssetThumbnailModule>;

const allowedLogoFiles = [
  'bet-bolt.png',
  'bettys.png',
  'burger-corner.png',
  'drink.png',
  'hero.png',
  'irl-logo-2d.png',
  'irl-logo-3d.png',
  'irl-text-default.png',
  'knead.png',
  'parsley.png',
  'spin.png',
  'sportbet.png',
] as const;

const backgroundFileNames = [
  'bet-bolt.png',
  'bettys-brekkie.png',
  'black-molecule-chain-corner.png',
  'black-topographic-lines.png',
  'blank-white-background.png',
  'blank-white-background-2.png',
  'blue-3d-bubble-cluster.png',
  'blue-brush-stroke-speckles.png',
  'blue-line-swirl-fan.png',
  'blue-maze-corners.png',
  'blue-pixel-corner.png',
  'blue-plus-corners.png',
  'blue-purple-ink-bloom-1.png',
  'blue-purple-ink-bloom-2.png',
  'blue-sketch-crosshatch.png',
  'blue-topographic-lines.png',
  'burger-corner.png',
  'drink.png',
  'emerald-glass-ribbon.png',
  'gray-pixel-corner.png',
  'gray-vertical-stripes.png',
  'green-maze-corners.png',
  'green-texture-sphere.png',
  'hero.png',
  'knead-bakery.png',
  'light-blue-halftone-sphere.png',
  'magenta-nebula-cloud.png',
  'mint-circle-pattern.png',
  'mint-circle-pattern-2.png',
  'mint-corner-step-frame.png',
  'mint-neon-smoke-side.png',
  'mint-step-grid-cluster.png',
  'mint-wave-stripes.png',
  'neon-wireframe-cube.png',
  'orange-purple-hex-cluster.png',
  'parsley.png',
  'pastel-bubble-cluster.png',
  'pastel-ribbon-waves.png',
  'pastel-sphere-splash-1.png',
  'pastel-sphere-splash-2.png',
  'pink-3d-cube-gradient.png',
  'pink-green-dot-bands.png',
  'pink-ink-wisps.png',
  'pink-line-lattice.png',
  'plain-white-background.png',
  'plain-white-background-2.png',
  'purple-cube-explosion-gradient.png',
  'purple-glass-cube-gradient.png',
  'purple-watercolor-column.png',
  'run.png',
  'spin.png',
  'sportbet.png',
  'teal-blocks-footer.png',
  'vertical-blue-smoke-plume.png',
  'white-faceted-paper.png',
] as const;

const logoAssets: DefaultAsset[] = allowedLogoFiles
  .map((fileName) => {
    const identifier = fileName.replace(/\.png$/, '');
    const displayName = formatDisplayName(identifier);
    const asset = createAsset(
      `default-logo-${identifier}`,
      displayName,
      'logo',
      `logos/${fileName}`,
      '',
    );

    return {
      ...asset,
      thumbnailUrl: logoOptionThumbnail(fileName),
    };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

const mapMarkerAssets = buildAssetsFromDirectory({
  modules: markerThumbnailModules,
  storageFolder: 'markers',
  idPrefix: 'default-map-marker',
  type: 'mapMarker',
});

// Background options now use their remote asset URLs directly instead of
// bundling local background thumbnails into the repo.
const backgroundAssets: DefaultAsset[] = backgroundFileNames
  .map((fileName) => {
    const identifier = fileName.replace(/\.png$/, '');
    const displayName = formatDisplayName(identifier);
    const asset = createAsset(
      `default-background-${identifier}`,
      `Background ${displayName}`,
      'background',
      `backgrounds/${fileName}`,
      '',
    );

    return {
      ...asset,
      thumbnailUrl: backgroundOptionThumbnail(fileName),
    };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

// For AR assets, we need to map the thumbnails to their corresponding GLB files
const defaultArAssets: DefaultAsset[] = Object.entries(arThumbnailModules)
  .map(([modulePath, module]) => {
    const fileName = modulePath.substring(modulePath.lastIndexOf('/') + 1);
    const identifier = fileName.replace(/\.png$/, '');

    let glbName = identifier;
    if (identifier === 'minecreft-creeper') glbName = 'minecraft-creeper';
    if (identifier === 'irl-r') glbName = 'inner-reality';

    return {
      id: `default-ar-${identifier}`,
      identifier,
      name: formatDisplayName(identifier),
      type: 'ar' as const,
      glbName,
      thumbnail: module.default,
    };
  })
  .filter(item => item.identifier !== 'irl-r')
  .map(item => createAsset(
    item.id,
    item.name,
    'ar',
    `3d-assets/${item.glbName}.glb`,
    item.thumbnail,
  ))
  .sort((a, b) => a.name.localeCompare(b.name));

export const defaultAssets: DefaultAsset[] = [
  ...defaultArAssets,
  ...backgroundAssets,
  ...mapMarkerAssets,
  ...logoAssets,
];
